import { ButtonClickEvent, ReactInput } from "@storeblocks/input";
import { FieldInputProps, useField } from "formik";
import React, { ChangeEvent, ReactElement, ReactNode } from "react";

import { WrapLgMarginBottom } from "App/global-style";
import { useComponentTexts } from "Store/texts/hooks";
import { inputTrackId } from "Utils/analytics";
import { useDebouncedInputField } from "Utils/debounce/useDebounce";
import { StyledStoreblocksInput } from "./inputStyled";
import { InputMode } from "./inputTypes";

export interface InputProps {
  inputMode: InputMode;
  debounced?: boolean;
  disabled?: boolean;
  error?: string;
  hint?: string | ReactNode;
  id: string;
  label: ReactNode;
  large?: boolean;
  medium?: boolean;
  name: string;
  as?: "input" | "textarea";
  rows?: number;
  fluid?: boolean;
  placeholder?: string;
  small?: boolean;
  type?: string;
  onChange?: (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    field?: FieldInputProps<any>,
  ) => void;
  icon?: string;
  onIconClick?: ButtonClickEvent;
  suffix?: string;
  inputProps?: ReactInput;
  labelPopover?: ReactElement;
}

export const Input = ({
  debounced = false,
  disabled = false,
  error,
  hint,
  id,
  label,
  large,
  medium,
  fluid,
  name,
  as,
  rows,
  placeholder,
  small,
  type,
  onChange,
  icon,
  onIconClick,
  suffix,
  inputProps,
  inputMode,
  labelPopover,
}: InputProps): React.ReactElement => {
  const [debouncedFieldValue, handleDebouncedOnChange] = useDebouncedInputField(
    id,
    500,
  );
  const [field, { touched }] = useField(name);
  const [
    {
      input: { readMore, readLess },
    },
    lang,
  ] = useComponentTexts();

  const innerValue = !debounced ? field.value : debouncedFieldValue;

  const handleOnChange = (e: ChangeEvent<HTMLInputElement>): void => {
    onChange && onChange(e, field);
    field.onChange(e);
  };

  const handleDebouncedOnChangeWrapper = (
    e: ChangeEvent<HTMLInputElement>,
  ): void => {
    handleDebouncedOnChange(e, () => onChange && onChange(e, field));
  };

  return (
    <WrapLgMarginBottom>
      <StyledStoreblocksInput
        disabled={disabled}
        error={error && touched ? error : undefined}
        hint={hint}
        id={id}
        name={name}
        rows={rows}
        forwardedAs={as}
        label={label}
        large={large}
        medium={medium}
        placeholder={placeholder}
        small={small}
        fluid={fluid}
        type={type}
        onChange={debounced ? handleDebouncedOnChangeWrapper : handleOnChange}
        icon={icon}
        onIconClick={onIconClick}
        value={typeof innerValue !== "undefined" ? innerValue : ""}
        suffix={suffix}
        inputMode={inputMode}
        inputProps={
          inputProps
            ? {
                ...inputProps,
                onBlur: (e) => {
                  inputProps.onBlur && inputProps.onBlur(e);
                  field.onBlur(e);
                },
              }
            : undefined
        }
        hintProps={{
          maxLines: 8,
          readMoreLabel: readMore[lang],
          readLessLabel: readLess[lang],
        }}
        trackid={inputTrackId(id)}
        labelPopover={labelPopover}
      />
    </WrapLgMarginBottom>
  );
};
